import { verifyOtp } from '@/api/otp';
import MetallicPillButton from '@/components/Base/MetallicPillButton';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import { SurveyBrownPanel, SurveyBrownPanelInset } from '@/components/layout/SurveyBrownPanel';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { useAppSelector } from '@/stores/hooks';
import { selectSurveyScreening, selectUniqueCodeDraft } from '@/stores/selectors';
import { displayFullMalaysiaMobile } from '@/utils/malaysiaMobile';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

/** Must match `MIN_OTP_VERIFY_DELAY_MS` in `packages/backend/src/routes/otpRoutes.ts`. */
const OTP_VERIFY_MIN_WAIT_MS = 5_000;

const lightInputClassName =
  'min-w-0 w-full rounded-sm border border-slate-300/90 bg-white px-3 py-2.5 text-[16px] font-semibold text-slate-900 placeholder:font-normal placeholder:text-slate-400 outline-none ring-0 focus:border-slate-400 focus:ring-1 focus:ring-slate-300';

export default function Otp() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const { mobileLocalDigits, customerUuid, otpSentAtMs } = useAppSelector(selectUniqueCodeDraft);
  const [otpCode, setOtpCode] = useState('');
  const [otpWarning, setOtpWarning] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [waitSecondsLeft, setWaitSecondsLeft] = useState(0);

  useEffect(() => {
    const tick = () => {
      if (otpSentAtMs == null) {
        setWaitSecondsLeft(0);
        return;
      }
      const msLeft = OTP_VERIFY_MIN_WAIT_MS - (Date.now() - otpSentAtMs);
      setWaitSecondsLeft(Math.max(0, Math.ceil(msLeft / 1000)));
    };
    tick();
    const id = window.setInterval(tick, 300);
    return () => window.clearInterval(id);
  }, [otpSentAtMs]);

  if (surveyLanguage == null) {
    return <Navigate to="/" replace />;
  }
  if (surveyScreening == null) {
    return <Navigate to="/age_smoker" replace />;
  }
  if (!mobileLocalDigits || mobileLocalDigits.replace(/\D/g, '').length === 0) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!customerUuid) {
    return <Navigate to="/uniquecode" replace />;
  }

  const handleSubmit = async () => {
    if (waitSecondsLeft > 0) {
      return;
    }
    const trimmed = otpCode.trim();
    if (!trimmed) {
      setOtpWarning(t.otp.emptyWarning);
      return;
    }
    setOtpWarning(null);
    setIsSubmitting(true);
    try {
      const result = await verifyOtp({ customerUuid, otp: trimmed });
      if (result.ok === false) {
        setOtpWarning(result.error);
      } else if (result.ok) {
        navigate('/smokerprofile');
      }
    } catch {
      setOtpWarning(t.otp.verifyError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex min-h-0 flex-1 flex-col overflow-y-auto">
        <div className="mx-auto mt-[25vh] flex w-full max-w-full flex-col py-8">
          <SurveyBrownPanelInset>
            <p className="m-0 text-left text-[16px] font-normal leading-snug text-white/95 sm:text-[17px]">
              <span className="block">{t.otp.sentMessage}</span>
              <span className="mt-1 block font-semibold tabular-nums">
                {displayFullMalaysiaMobile(mobileLocalDigits)}
              </span>
            </p>
          </SurveyBrownPanelInset>

          <SurveyBrownPanelInset className="mt-4">
            {waitSecondsLeft > 0 ? (
              <div className="rounded-lg border border-amber-400/40 bg-amber-950/25 px-3 py-3 text-center sm:px-4">
                <p className="m-0 text-[28px] font-semibold tabular-nums leading-none text-amber-100 sm:text-[32px]">
                  {waitSecondsLeft}
                  <span className="text-[14px] font-medium text-amber-100/85 sm:text-[15px]">s</span>
                </p>
                <p className="m-0 mt-2 text-left text-[13px] font-normal leading-snug text-white/90 sm:text-[14px]">
                  {t.otp.waitBeforeSubmit.replace(/\{\{seconds\}\}/g, String(waitSecondsLeft))}
                </p>
              </div>
            ) : (
              <p className="m-0 text-center text-[13px] font-normal leading-snug text-emerald-100/95 sm:text-[14px]">
                {t.otp.readyToSubmit}
              </p>
            )}
          </SurveyBrownPanelInset>

          <SurveyBrownPanelInset className="mt-6">
            <SurveyBrownPanel className="!p-4">
              <div className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2 gap-y-5 sm:gap-x-3">
                <label
                  htmlFor="otp-code"
                  className="text-left text-[16px] font-medium text-white/95 sm:text-[17px]"
                >
                  {t.otp.otpCodeLabel}
                </label>
                <input
                  id="otp-code"
                  type="text"
                  name="otpCode"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder=""
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className={`${lightInputClassName} tabular-nums tracking-wide`}
                />
              </div>
            </SurveyBrownPanel>
          </SurveyBrownPanelInset>

          <SurveyBrownPanelInset className="mt-10">
            <div className="flex flex-col items-center gap-3">
              {otpWarning != null ? <SurveyValidationAlert>{otpWarning}</SurveyValidationAlert> : null}
              <MetallicPillButton
                type="button"
                size="sm"
                className="metallic-pill--lang-home"
                disabled={isSubmitting || waitSecondsLeft > 0}
                onClick={() => void handleSubmit()}
              >
                {t.otp.submit}
              </MetallicPillButton>
            </div>
          </SurveyBrownPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
